import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/tmc/Layout";
import { ShieldAlert, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export const SECTIONS = [
  [`1. Charakter usługi`, `The Money Club Bot („Oprogramowanie") to zautomatyzowany, półautomatyczny i ręczny system tradingowy łączący się z Twoim własnym rachunkiem MetaTrader 5 za pośrednictwem MetaAPI. Oprogramowanie jest narzędziem technicznym. NIE jest doradcą inwestycyjnym, brokerem ani zarządzającym aktywami i nie świadczy usług doradztwa finansowego.`],
  [`2. Wysokie ryzyko straty`, `Handel na rynkach lewarowanych (Forex, metale, indeksy, kryptowaluty, CFD) wiąże się z BARDZO WYSOKIM RYZYKIEM i może doprowadzić do utraty CAŁOŚCI zdeponowanego kapitału, a w niektórych przypadkach więcej niż wpłacona kwota. Handlujesz wyłącznie na własne ryzyko i własną odpowiedzialność.`],
  [`3. Brak gwarancji zysków`, `Nie udzielamy ŻADNEJ gwarancji zysku, skuteczności, wyników ani pewnego dochodu. Wyniki historyczne, backtesty i przykłady NIE stanowią obietnicy przyszłych rezultatów. Każda decyzja o uruchomieniu bota lub złożeniu zlecenia należy wyłącznie do Ciebie.`],
  [`4. POLITYKA BRAKU ZWROTÓW`, `Zakup dostępu / licencji do The Money Club Bot jest OSTATECZNY i BEZZWROTNY. Ze względu na cyfrowy charakter produktu i natychmiastowy dostęp do funkcji, NIE PRZYSŁUGUJE PRAWO DO ZWROTU, odstąpienia od umowy ani reklamacji dotyczącej wyników handlowych, po dokonaniu płatności i aktywacji licencji. Akceptując niniejszy regulamin wyraźnie zrzekasz się prawa do zwrotu środków.`],
  [`5. Twoje dane MetaAPI`, `Wprowadzasz własny token MetaAPI oraz Account ID. Token jest przechowywany w formie zaszyfrowanej i nigdy nie jest zwracany do przeglądarki. Ponosisz pełną odpowiedzialność za bezpieczeństwo swojego konta brokerskiego i danych logowania.`],
  [`6. Ograniczenie odpowiedzialności`, `W maksymalnym zakresie dozwolonym przez prawo, twórcy i operatorzy The Money Club Bot NIE ponoszą odpowiedzialności za jakiekolwiek straty, utracone zyski, poślizgi cenowe, awarie brokera, przerwy w działaniu MetaAPI/serwera, opóźnienia ani błędy wykonania zleceń.`],
  [`7. Zgodność i wiek`, `Oświadczasz, że masz ukończone 18 lat, korzystanie z automatycznego handlu jest legalne w Twojej jurysdykcji oraz że akceptujesz regulamin swojego brokera.`],
];

export default function Terms() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [checked, setChecked] = useState(false);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    if (!checked || signature.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post("/terms/accept", { signature: signature.trim() });
      await refresh();
      toast.success("Regulamin zaakceptowany");
      nav("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Błąd"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background terminal-bg flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-3xl fade-up" data-testid="terms-gate-modal">
        <div className="flex items-center justify-center mb-6"><Logo size={48} /></div>
        <div className="tmc-card overflow-hidden">
          <div className="bg-primary/10 border-b border-primary/30 px-6 py-5 flex items-start gap-3">
            <ShieldAlert className="w-6 h-6 text-primary shrink-0 mt-0.5" />
            <div>
              <h1 className="font-head text-lg sm:text-xl font-bold text-white">Regulamin i Oświadczenie o Ryzyku</h1>
              <p className="text-sm text-muted-foreground mt-1">Wymagana akceptacja przed dostępem do bota. Polityka braku zwrotów.</p>
            </div>
          </div>

          <div className="max-h-[46vh] overflow-y-auto px-6 py-5 space-y-4 text-sm" data-testid="terms-content">
            {SECTIONS.map(([title, body]) => (
              <div key={title}>
                <h3 className={`font-head font-semibold mb-1 ${title.includes("BRAKU ZWROT") ? "text-primary" : "text-white"}`}>{title}</h3>
                <p className="text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground/70 font-mono pt-2 border-t border-border">Wersja regulaminu 1.0 · Data i adres IP akceptacji zostaną zapisane.</p>
          </div>

          <div className="border-t border-border px-6 py-5 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="terms-checkbox"
                className="mt-1 w-4 h-4 accent-[hsl(38,78%,56%)]" />
              <span className="text-sm text-gray-200">Przeczytałem/am i w pełni akceptuję powyższy regulamin, w tym <b className="text-primary">politykę braku zwrotów</b> oraz oświadczenie o wysokim ryzyku straty kapitału.</span>
            </label>
            <div>
              <label className="text-xs text-muted-foreground font-mono">Podpis cyfrowy — wpisz swoje imię i nazwisko</label>
              <input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Jan Kowalski" data-testid="terms-signature-input"
                className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-primary" />
            </div>
            <button onClick={accept} disabled={!checked || signature.trim().length < 3 || busy} data-testid="terms-accept-btn"
              className="w-full bg-primary hover:opacity-90 text-black font-bold rounded-lg px-4 py-3 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Akceptuję i wchodzę do terminala
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
